/**
 * FaqPage
 * Public FAQ & Guides page accessible at /faq without authentication.
 * Uses native <details>/<summary> for accordion, matching the landing page pattern.
 */

import { buildFaqJsonLd, SEO_FAQ } from '@config/seoConfig';
import { useSEO } from '@hooks/useSEO';
import type { FaqCategory, FaqItem, GuideSection } from '@parlay-picker/shared/faqData';
import { FAQ_CATEGORIES, GUIDE_SECTIONS } from '@parlay-picker/shared/faqData';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

const toSlug = (text: string): string => text.toLowerCase().replace(/\s+/g, '-');

function FaqAccordionItem({ item }: { item: FaqItem }): ReactElement {
  return (
    <details className="group border-b border-theme-border last:border-b-0">
      <summary className="flex justify-between items-center py-4 cursor-pointer text-theme-text-secondary hover:text-white transition-colors">
        <span className="text-sm font-medium pr-4">{item.question}</span>
        <span className="text-theme-text-tertiary group-open:rotate-180 transition-transform shrink-0">
          ▼
        </span>
      </summary>
      <p className="pb-4 text-sm text-theme-text-tertiary leading-relaxed">{item.answer}</p>
    </details>
  );
}

function FaqCategorySection({ category }: { category: FaqCategory }): ReactElement {
  return (
    <div id={toSlug(category.title)}>
      <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
        <span>{category.icon}</span>
        {category.title}
      </h3>
      <div className="bg-theme-surface rounded-lg border border-theme-border px-4">
        {category.items.map((item) => (
          <FaqAccordionItem key={item.question} item={item} />
        ))}
      </div>
    </div>
  );
}

function GuideSectionBlock({ guide }: { guide: GuideSection }): ReactElement {
  return (
    <div id={guide.slug} className="scroll-mt-20">
      <h3 className="text-lg font-semibold text-white mb-4">{guide.title}</h3>
      <div className="bg-theme-surface rounded-lg border border-theme-border p-5 space-y-4">
        {guide.paragraphs.map((p, i) => (
          <p
            key={`${guide.slug}-${i}`}
            className="text-sm text-theme-text-secondary leading-relaxed"
          >
            {p}
          </p>
        ))}
      </div>
    </div>
  );
}

export function FaqPage(): ReactElement {
  const faqJsonLd = useMemo(() => buildFaqJsonLd(FAQ_CATEGORIES), []);
  useSEO({ ...SEO_FAQ, jsonLd: faqJsonLd });

  return (
    <div data-testid="faq-page" className="min-h-screen bg-theme-bg text-theme-text">
      {/* Header */}
      <header className="border-b border-theme-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <Link
              to="/"
              className="text-xl font-bold text-white hover:text-theme-primary transition-colors"
            >
              Parlay Picker
            </Link>
            <p className="text-sm text-theme-text-tertiary">FAQ & Guides</p>
          </div>
          <Link
            to="/"
            className="text-sm text-theme-text-secondary hover:text-white transition-colors"
          >
            ← Back
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Page title */}
        <div className="text-center mb-10">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Frequently Asked Questions
          </h1>
          <p className="text-theme-text-secondary">
            Everything you need to know about Parlay Picker
          </p>
        </div>

        {/* Quick nav */}
        <nav className="mb-10 bg-theme-surface rounded-lg border border-theme-border p-4">
          <p className="text-xs font-semibold text-theme-text-tertiary uppercase tracking-wider mb-3">
            Jump to
          </p>
          <div className="flex flex-wrap gap-2">
            {FAQ_CATEGORIES.map((cat) => (
              <a
                key={cat.title}
                href={`#${toSlug(cat.title)}`}
                className="text-xs px-3 py-1.5 rounded-full bg-theme-bg border border-theme-border text-theme-text-secondary hover:text-white hover:border-theme-primary transition-colors"
              >
                {cat.icon} {cat.title}
              </a>
            ))}
            {GUIDE_SECTIONS.map((guide) => (
              <a
                key={guide.slug}
                href={`#${guide.slug}`}
                className="text-xs px-3 py-1.5 rounded-full bg-theme-bg border border-theme-border text-theme-text-secondary hover:text-white hover:border-theme-primary transition-colors"
              >
                📖 {guide.title}
              </a>
            ))}
          </div>
        </nav>

        {/* FAQ Accordion sections */}
        <section className="space-y-8 mb-12">
          {FAQ_CATEGORIES.map((category) => (
            <FaqCategorySection key={category.title} category={category} />
          ))}
        </section>

        {/* Guides */}
        <section className="space-y-8 mb-12">
          <h2 className="text-xl font-bold text-white border-b border-theme-border pb-3">
            In-Depth Guides
          </h2>
          {GUIDE_SECTIONS.map((guide) => (
            <GuideSectionBlock key={guide.slug} guide={guide} />
          ))}
        </section>

        {/* CTA */}
        <div className="text-center py-8 border-t border-theme-border">
          <p className="text-theme-text-secondary mb-4">Ready to get started?</p>
          <Link
            to="/"
            className="inline-block px-6 py-2 bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill font-medium rounded-lg transition-colors"
          >
            Try Parlay Picker — Free
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800 p-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex justify-center gap-6 mb-4 text-sm">
            <Link
              to="/contact"
              className="text-theme-text-tertiary hover:text-theme-text-secondary transition"
            >
              Contact
            </Link>
            <Link
              to="/"
              className="text-theme-text-tertiary hover:text-theme-text-secondary transition"
            >
              Home
            </Link>
          </div>
          <p className="text-center text-gray-600 text-xs">
            Gamble responsibly. This is not financial advice.
          </p>
        </div>
      </footer>
    </div>
  );
}
